<template>
  <div>
    <b-modal
      :id="modalId"
      size="lg"
      :title="modalTitle"
      :ok-title="$t('Close')"
      ok-only
      ok-variant="secondary"
      scrollable
      @shown="generate"
      @hidden="onHidden"
    >
      <div class="ai-image-generation-modal">
        <!-- Source images -->
        <div v-if="sourceImages.length" class="mb-3">
          <p class="font-weight-bold mb-1">{{ $t('Source Images') }}</p>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="img in sourceImages" :key="img.uuid" class="source-thumbnail">
              <img :src="getImageThumbnail(img)" :alt="img.name" class="rounded border"/>
            </div>
          </div>
        </div>

        <!-- Extra prompt (hidden for now) -->
        <!--
        <div class="form-group mb-3">
          <label class="font-weight-bold">{{ $t('Extra Prompt (optional)') }}</label>
          <textarea
            v-model="extraPrompt"
            class="form-control"
            rows="2"
            :placeholder="$t('Add extra instructions for AI generation...')"
            :disabled="loading"
          />
        </div>
        -->

        <!-- Generate controls -->
        <div class="d-flex align-items-center mb-3">
          <b-button
            variant="primary"
            :disabled="loading || !sourceImages.length"
            @click="generate()"
          >
            <b-spinner v-if="loading" small class="mr-1"/>
            {{ loading ? $t('Generating...') : $t('Generate') }}
          </b-button>
          <span v-if="generatedImages.length && !loading" class="ml-3 text-muted small">
            {{ $t('{count} image(s) generated', {count: generatedImages.length}) }}
          </span>
        </div>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <!-- Generated images grid -->
        <div v-if="generatedImages.length || pendingCount > 0">
          <p class="font-weight-bold mb-2">{{ $t('Generated Images') }}</p>
          <div class="d-flex flex-wrap">
            <div
              v-for="(img, idx) in generatedImages"
              :key="idx"
              class="generated-image-card mr-3 mb-3"
            >
              <div
                class="generated-thumb-wrapper"
                :class="{'is-selected': isSelected(idx)}"
                @click="openLightbox(idx)"
              >
                <img
                  :src="img.preview_url"
                  :alt="`Generated ${idx + 1}`"
                  class="generated-thumb rounded"
                  :class="{'ring-reference': expandingFrom && expandingFrom === img}"
                />
                <div
                  class="thumb-checkbox"
                  :class="{'thumb-checkbox--checked': isSelected(idx)}"
                  @click.stop="toggleSelect(idx)"
                  :title="isSelected(idx) ? $t('Deselect') : $t('Select')"
                >
                  <font-awesome-icon v-if="isSelected(idx)" icon="check"/>
                </div>
              </div>
              <div class="mt-2">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="loading"
                  @click.stop="expandFromImage(img)"
                >
                  {{ $t('Show more like this') }}
                </b-button>
              </div>
            </div>

            <!-- Skeleton cards: one per pending request -->
            <template v-if="pendingCount > 0">
              <div v-for="n in pendingCount" :key="`skeleton-${n}`" class="generated-image-card mr-3 mb-3">
                <div class="generated-thumb-skeleton rounded"/>
              </div>
            </template>
          </div>

          <!-- Confirm selection bar -->
          <div v-if="selectedIndices.length" class="confirm-bar d-flex align-items-center justify-content-between p-3 rounded">
            <span>
              <strong>{{ selectedIndices.length }}</strong>
              {{ $t('image(s) selected') }}
            </span>
            <b-button
              variant="success"
              @click="confirmSelection"
            >
              {{ $t('Add to Inventory') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Lightbox modal (portal-mounted to body, always above parent modal) -->
    <b-modal
      v-if="lightboxImage"
      v-model="showLightbox"
      :id="`${modalId}-lightbox`"
      hide-header
      hide-footer
      size="xl"
      modal-class="ai-lightbox-modal"
      content-class="ai-lightbox-modal-content"
      body-class="ai-lightbox-modal-body"
      centered
      @hidden="onLightboxHidden"
    >
      <div class="ai-lightbox-content" @click.self="closeLightbox">
        <div class="ai-lightbox-img-wrapper">
          <img
            :src="lightboxImage.preview_url"
            class="ai-lightbox-img"
            :alt="`Generated ${lightboxIdx + 1}`"
          />
        </div>
        <div class="ai-lightbox-nav">
          <button class="ai-lightbox-nav-btn" :disabled="lightboxIdx === 0" @click="lightboxIdx--">&larr;</button>
          <span class="ai-lightbox-counter">{{ lightboxIdx + 1 }} / {{ generatedImages.length }}</span>
          <button class="ai-lightbox-nav-btn" :disabled="lightboxIdx === generatedImages.length - 1" @click="lightboxIdx++">
            &rarr;
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import InventoryService from '../../InventoryService'

export default {
  name: 'AiImageGenerationModal',
  props: {
    modalId: {
      type: String,
      default: 'ai-image-generation-modal',
    },
    inventoryUuid: {
      type: String,
      required: true,
    },
    sourceImages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      extraPrompt: '',
      loading: false,
      pendingCount: 0,
      error: null,
      generatedImages: [],
      selectedIndices: [],
      expandingFrom: null,
      lightboxIdx: null,
      showLightbox: false,
    }
  },
  computed: {
    modalTitle() {
      return this.$t('Generate AI Images')
    },
    lightboxImage() {
      return this.lightboxIdx !== null ? this.generatedImages[this.lightboxIdx] : null
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    getImageThumbnail(img) {
      if (!img) return ''
      if (img.preview_url) return img.preview_url
      const data = img.data || {}
      for (const size of ['thumb', 'sm', 'original', 'lg', 'xlg']) {
        if (data[size] && data[size].url) return data[size].url
      }
      return ''
    },

    async generate() {
      this.loading = true
      this.error = null
      this.generatedImages = []
      this.selectedIndices = []
      this.expandingFrom = null
      this.lightboxIdx = null
      this.showLightbox = false
      await this._runTwoRequests(null)
      this.loading = false
    },

    async expandFromImage(referenceImg) {
      this.loading = true
      this.error = null
      this.expandingFrom = referenceImg
      await this._runTwoRequests(referenceImg.preview_url)
      this.loading = false
    },

    async _runTwoRequests(referenceUrl) {
      const imageUuids = this.sourceImages.map(img => img.uuid)
      this.pendingCount = 2

      const makeOne = async () => {
        try {
          const response = await InventoryService.generateAiImages(
            this.inventoryUuid,
            imageUuids,
            this.extraPrompt,
            1,
            referenceUrl,
          )
          this.generatedImages = [...this.generatedImages, ...(response.body || [])]
        } catch (err) {
          this.error = (err.body && err.body.detail) || this.$t('AI image generation failed. Please try again.')
        } finally {
          this.pendingCount = Math.max(0, this.pendingCount - 1)
        }
      }

      await Promise.allSettled([makeOne(), makeOne()])
    },

    isSelected(idx) {
      return this.selectedIndices.includes(idx)
    },

    toggleSelect(idx) {
      if (this.isSelected(idx)) {
        this.selectedIndices = this.selectedIndices.filter(i => i !== idx)
      } else {
        this.selectedIndices = [...this.selectedIndices, idx]
      }
    },

    openLightbox(idx) {
      this.lightboxIdx = idx
      this.showLightbox = true
    },

    closeLightbox() {
      this.showLightbox = false
    },

    onLightboxHidden() {
      this.lightboxIdx = null
    },

    onKeydown(e) {
      if (!this.showLightbox) return
      if (e.key === 'Escape') this.closeLightbox()
      if (e.key === 'ArrowRight' && this.lightboxIdx < this.generatedImages.length - 1) this.lightboxIdx++
      if (e.key === 'ArrowLeft' && this.lightboxIdx > 0) this.lightboxIdx--
    },

    confirmSelection() {
      if (!this.selectedIndices.length) return
      const selectedImages = this.selectedIndices.map(i => this.generatedImages[i])

      selectedImages.forEach((img, i) => {
        const blob = this._base64ToBlob(img.preview_url)
        this.$emit('confirmed', {
          uuid: null,
          to_be_deleted: false,
          ai_generated: true,
          base64_src: img.preview_url,
          file: {type: blob.type, name: `ai_generated_${i + 1}.jpg`},
          blob,
        })
      })

      this.$bvModal.hide(this.modalId)
    },

    _base64ToBlob(dataUrl) {
      const [header, data] = dataUrl.split(',')
      const mime = header.match(/:(.*?);/)[1]
      const binary = atob(data)
      const buffer = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) {
        buffer[i] = binary.charCodeAt(i)
      }
      return new Blob([buffer], {type: mime})
    },

    onHidden() {
      this.extraPrompt = ''
      this.loading = false
      this.pendingCount = 0
      this.error = null
      this.generatedImages = []
      this.selectedIndices = []
      this.expandingFrom = null
      this.lightboxIdx = null
      this.showLightbox = false
      this.$emit('hidden')
    },
  },
}
</script>

<style scoped>
.source-thumbnail img {
  width: 72px;
  height: 72px;
  object-fit: cover;
}

/* Grid image */
.generated-thumb-wrapper {
  position: relative;
  display: inline-block;
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  border: 3px solid transparent;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.generated-thumb-wrapper:hover {
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.5);
}

.generated-thumb-wrapper.is-selected {
  border-color: #28a745;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.4);
}

.generated-thumb {
  width: 180px;
  height: 180px;
  object-fit: cover;
  display: block;
}

.generated-image-card {
  display: flex;
  flex-direction: column;
}

.generated-thumb-skeleton {
  width: 180px;
  height: 180px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.thumb-checkbox {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid #aaa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #28a745;
  transition: background 0.15s, border-color 0.15s;
  z-index: 1;
}

.thumb-checkbox:not(.thumb-checkbox--checked):hover {
  border-color: #28a745;
  background: #fff;
}

.thumb-checkbox--checked {
  background: #28a745;
  border-color: #28a745;
  color: #fff;
}

.thumb-checkbox--checked:hover {
  background: #218838;
  border-color: #218838;
}

.ring-reference {
  outline: 3px solid #007bff;
  outline-offset: 2px;
}

.confirm-bar {
  background: #f0fff4;
  border: 1px solid #28a745;
}

.gap-2 {
  gap: 0.5rem;
}

</style>

<style>
.ai-lightbox-modal .modal-dialog {
  max-width: 95vw;
}

.ai-lightbox-modal-content {
  background: rgba(0, 0, 0, 0.92) !important;
  border: none !important;
}

.ai-lightbox-modal-body {
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-lightbox-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  width: 100%;
}

.ai-lightbox-img-wrapper {
  position: relative;
  border-radius: 6px;
  border: 4px solid transparent;
  transition: border-color 0.15s, box-shadow 0.15s;
  display: inline-block;
}

.ai-lightbox-img {
  max-width: 85vw;
  max-height: 75vh;
  object-fit: contain;
  display: block;
  border-radius: 4px;
}

.ai-lightbox-nav {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ai-lightbox-nav-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  font-size: 1.25rem;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}

.ai-lightbox-nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
}

.ai-lightbox-nav-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.ai-lightbox-counter {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
}
</style>
